/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : LegalStandardChapter.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Divider, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  HandleApiActions,
  legalRequirements,
  userManagementApis,
} from "../../../redux/actions";
import CustomSelectV3 from "../../../components/customComponentsV2/common/select/CustomSelectV3";
import dayjs from "dayjs";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";

function LegalStandardChapter({ selectedRoleId }) {
  const dispatch = useDispatch();
  const legalStandardListFromStore = useSelector(
    (state) => state.userManagementReducer.legalStandardDetails,
  );
  console.log(
    "legalStandardListFromStorelegalStandardListFromStore",
    legalStandardListFromStore,
  );
  const [legalStandardsData, setLegalStandardsData] = useState([]);
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [standardOptionsList, setStandardOptionsList] = useState([]);
  const [chapter, setChapter] = useState("");
  console.log("legalStandardsDatalegalStandardsData", legalStandardsData);
  const items = useMemo(
    () =>
      legalStandardsData?.find(
        (data) => data.standard_number === selectedStandard,
      )?.items || [],
    [legalStandardsData, selectedStandard],
  );
  console.log("itemsitemsitemsitemsitemsitems", items);
  const [selectedVersion, setSelectedVersion] = useState(
    items?.[items.length - 1]?.version || "",
  );
  const [chaptersOptionsList, setChaptersOptionsList] = useState([]);
  const standard = useMemo(
    () => items.find((i) => i.ls_id === selectedVersion) || {},
    [items, selectedVersion],
  );


  useEffect(() => {
    refreshLegalStandards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStandard]);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...userManagementApis.get_roleLS_ddh_ucm,
        api_name: `/UCM/role-legal-chapters/roles/${selectedRoleId?.role_id}/legal-chapters`,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (standard?.chapters?.length > 0) {
      const filteredChaptersOptionsListS =
        standard?.chapters
          ?.filter(
            (option) =>
              !legalStandardListFromStore?.legalChapters?.some(
                (assignedChapter) =>
                  String(assignedChapter?.legal_chapter) === String(option) &&
                  Number(assignedChapter?.legal_standard_id) ===
                    Number(selectedVersion),
              ),
          )
          ?.map((chapter) => ({
            value: chapter,
            label: chapter,
          })) || [];
      setChaptersOptionsList(filteredChaptersOptionsListS || []);
    }
  }, [standard, legalStandardListFromStore, selectedVersion]);

  console.log("chapters----", chaptersOptionsList);
  const handleGetLegalStandardList = async () => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...legalRequirements.get_legal_standard_main_details,
          params: {
            page_number: 1,
            page_size: 2500,
            sort_by: "ASC",
            sort_column: "created_at",
          },
          show_toast: false,
        }),
      ),
    );
    console.log("----Legal Standard Details ----", resp);
    return resp;
  };

  const groupAndSortLegalStandards = (data) => {
    if (!Array.isArray(data)) return [];

    const grouped = data.reduce((acc, item) => {
      const key = item.standard_number;
      if (!acc[key]) acc[key] = [];
      acc[key].push({
        standard_number: item.standard_number,
        version: item.version,
        revision: item.revision,
        ls_id: item.ls_id,
        ...item,
      });
      return acc;
    }, {});

    const sortedGroupedDetails = Object.entries(grouped).map(
      ([standard_number, items]) => ({
        standard_number,
        items: items.sort((a, b) => a.version - b.version),
      }),
    );

    return sortedGroupedDetails;
  };
  const refreshLegalStandards = async (defaultFirstSelect = true) => {
    const resp = await handleGetLegalStandardList();
    const sortedGroupedDetails = groupAndSortLegalStandards(resp?.data);

    console.log("sortedGroupedDetails", sortedGroupedDetails);
    setLegalStandardsData(sortedGroupedDetails);

    const standardList = sortedGroupedDetails?.map((el) => ({
      value: el.standard_number,
      label: el.standard_number,
    }));

    setStandardOptionsList(standardList);

  };

  const versionOptions = useMemo(() => {
    return [...items]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // descending
      .map((item) => ({
        label: `${item.version} - ${dayjs(item?.created_at).format(
          "DD MMM YYYY",
        )}`,
        value: item.ls_id,
      }));
  }, [items]);

  const handleOnCLickAttach = () => {
    let reqObj = {
      legalChapters: [
        {
          legalStandardId: selectedVersion,
          chapter: chapter,
        },
      ],
      reason: "Attaching legal standard chapter",
    };
    dispatch(
      HandleApiActions({
        ...userManagementApis.add_roleLS_ddh_ucm,
        api_name: `/UCM/role-legal-chapters/roles/${selectedRoleId?.role_id}/legal-chapters`,
        params: reqObj,
        show_toast: true,
      }),
    ).then(async () => {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...userManagementApis.get_roleLS_ddh_ucm,
            api_name: `/UCM/role-legal-chapters/roles/${selectedRoleId?.role_id}/legal-chapters`,
          }),
        ),
      );
      if (!resp?.error) {
        setSelectedStandard("");
        setSelectedVersion("");
        setChapter("");
        setChaptersOptionsList([]);
      }
    });
    console.log("reqObjreqObjreqObj", reqObj);
  };

  console.log("selectedVersionselectedVersionselectedVersion", selectedVersion);

  const handleDetach = (data) => {
    console.log("data to detach", data);
    let reqObj = {
      legalChapters: [
        {
          legalStandardId: data?.legal_standard_id,
          chapter: data?.legal_chapter,
        },
      ],
      reason: "Detaching legal standard chapter",
    };
    dispatch(
      HandleApiActions({
        ...userManagementApis.delete_roleLS_ddh_ucm,
        api_name: `/UCM/role-legal-chapters/roles/${selectedRoleId?.role_id}/legal-chapters`,
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      dispatch(
        HandleApiActions({
          ...userManagementApis.get_roleLS_ddh_ucm,
          api_name: `/UCM/role-legal-chapters/roles/${selectedRoleId?.role_id}/legal-chapters`,
        }),
      );
    });
  };
  console.log("selectedRoleIdselectedRoleId", selectedRoleId);

  const selectedChapters =
    legalStandardListFromStore?.legalChapters?.map(
      (item) => item?.legal_chapter,
    ) || [];

  console.log(
    "selectedChaptersselectedChaptersselectedChapters",
    selectedChapters,
  );
  console.log(
    chaptersOptionsList?.filter(
      (option) =>
        !selectedChapters.some(
          (selected) => String(selected) === String(option?.value),
        ),
    ),
  );
  return (
    <div>
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
            width: "100%",
          }}
        >
          <CustomSelectV3
            label={"Legal Standard"}
            enableSearch
            required={true}
            handleOnChange={(e) =>
              setSelectedStandard(e.target.value === "" ? null : e.target.value)
            }
            selectedValue={selectedStandard}
            optionsList={standardOptionsList || []}
            sltFldStyle={{
              minHeight: "45px",
              fontSize: "0.95rem",
              borderRadius: 0,
            }}
          />
          <CustomSelectV3
            required={"true"}
            label="Version"
            enableSearch
            selectedValue={selectedVersion}
            handleOnChange={(e) => setSelectedVersion(e.target.value)}
            optionsList={versionOptions}
            sltFldStyle={{
              width: "100%",
              minHeight: "45px",
              fontSize: "0.95rem",
              borderRadius: 0,
            }}
          />
        </Box>

        <CustomSelectV3
          required={true}
          label="Chapter"
          enableSearch
          selectedValue={chapter}
          handleOnChange={(e) => setChapter(e.target.value)}
          optionsList={chaptersOptionsList || []}
          sltFldStyle={{
            width: "100%",
            minHeight: "45px",
            fontSize: "0.95rem",
            mt: 1,
            borderRadius: 0,
          }}
        />
      </Box>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <CustomButtonV2
          disabled={!selectedStandard || !selectedVersion || !chapter}
          type="submit"
          btnName="Attach"
          handleOnClick={handleOnCLickAttach}
        />
      </Box>

      <Divider sx={{ my: 2 }} />
      <Box>
        <Typography sx={{ fontWeight: 500, textAlign: "center" }}>
          List of Standards/Chapters
        </Typography>
      </Box>
      <Box
        sx={{
          maxHeight: "30vh",
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#b0b0b0",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#888",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
            borderRadius: "8px",
          },
        }}
      >
        {legalStandardListFromStore?.legalChapters?.length > 0 ? (
          legalStandardListFromStore?.legalChapters?.map((data, idx) => {
            const cardKey =
              data?.id ??
              `${data?.legal_standard_id}-${data?.legal_chapter}-${idx}`;
            return (
              <Box key={cardKey}>
                <Box
                  sx={{
                    backgroundColor: "#f4f4f4",
                    px: 1,
                    py: 1,
                    mt: 1,
                    borderRadius: 1,
                    fontSize: "0.85rem",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Box>
                      <CustomButtonV2
                        type="submit"
                        btnName="Detach"
                        handleOnClick={() => handleDetach(data)}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      {[
                        {
                          title: "Name",
                          content: data?.legal_standard_name,
                        },
                        {
                          title: "Number",
                          content: data?.legal_standard_number,
                        },
                        {
                          title: "Chapter",
                          content: data?.legal_chapter,
                        },

                        {
                          title: "Version",
                          content: data?.version,
                        },
                      ]?.map((item) => (
                        <Typography
                          key={item.title}
                          sx={{
                            mb: 1,
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2, // 👈 number of lines
                            overflow: "hidden",
                          }}
                        >
                          <strong>{item.title}:</strong> {item.content || "-"}
                        </Typography>
                      ))}

                      <Box></Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })
        ) : (
          <Typography sx={{ fontWeight: 200, textAlign: "center" }}>
            No Standards/Chapters Found
          </Typography>
        )}
      </Box>
    </div>
  );
}

LegalStandardChapter.propTypes = {
  selectedRoleId: PropTypes.shape({
    role_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

export default LegalStandardChapter;
